import { Component, OnInit, Injector, AfterViewInit, Input } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { PageResolverService } from 'src/app/service/page-resolver.service';
import { Title } from '@angular/platform-browser';
import { SEOService, IPageMetaData } from 'src/app/service/seo.service';
import { Location, ViewportScroller } from '@angular/common';
import { IUProperty } from '../../interface/umbraco-property';
import { SiteSettingsService } from 'src/app/service/site-settings.service';
import { ISiteSettings } from '../../interface/site-settings';
import get from 'lodash.get';
import { ActivatedRoute, Router } from '@angular/router';
import { first, filter, takeUntil } from 'rxjs/operators';
import { PanelsService } from '../panels/panels.service';
import { IPictureData } from '../picture/picture.component';

export interface IPageData {
  title: IUProperty<string>;
  url: string;
  metaData: IPageMetaData;
  panels: IUProperty<any[]>;
  utmConfiguration: any;
  rightColumnPanels: IUProperty<any[]>;
  pageBackgroundImage: IPageBackgroundImage;
}

export interface IPageBackgroundImage {
  backgroundImageImage: IUProperty<IPictureData>;
  backgroundImageRemoveOverlay: IUProperty<boolean>;
}

@Component({
  selector: 'app-abstract-page',
  template: '',
  styles: ['']
})
export class AbstractPageComponent implements OnInit, AfterViewInit {

  @Input() data: IPageData = null;
  protected defaultTitle = '';
  protected siteSettings: ISiteSettings;
  protected alive$ = new Subject;

  constructor(
    protected pageResolverService: PageResolverService,
    protected titleService: Title,
    protected seoService: SEOService,
    protected location: Location,
    protected injector: Injector,
    protected siteSettingsService: SiteSettingsService,
    protected route: ActivatedRoute,
    protected viewportScroller: ViewportScroller,
    protected panelsService: PanelsService,
    protected router: Router
  ) { }

  async ngOnInit()
  {
    // this.data = this.route.snapshot.data.data as IPageData;

    const utmParams = this.getUtmParams(this.data);
    if (Object.keys(utmParams).length > 0) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: utmParams,
        queryParamsHandling: 'merge'
      });
    }

    this.siteSettings = await this.siteSettingsService.getSiteSettings();

    this.titleService.setTitle(this.resolvePageTitle(this.data, this.siteSettings));

    if (this.data !== null)
    {
      this.seoService.addTags(this.data.metaData);
      this.data.url && this.seoService.addCanonicalURL(this.data.url);
      this.data.url && this.seoService.updateStructuredData(this.data.url);
    }

    this.handleAnchor();
  }

  ngAfterViewInit() {
    this.updatePageBackground();
  }

  handleAnchor()
  {
    const anchor$ = this.route.fragment.pipe(first());
    const lastLoadedPanelAnchor$ = this.panelsService.state$.pipe(filter(i => i !== null));
    const targetAnchor$ = combineLatest(anchor$, lastLoadedPanelAnchor$, (targetAnchor, lastLoadedPanel) => {
      if (targetAnchor === lastLoadedPanel.anchor) return targetAnchor;
    }).pipe(takeUntil(this.alive$));

    targetAnchor$.subscribe((fragment: string) => {this.handleInitialAnchor(fragment); });
  }

  ngOnDestroy()
  {
    this.alive$.next();
    this.alive$.complete();
  }

  private resolvePageTitle(data: IPageData, settings: ISiteSettings): string
  {
    let first = get(data, 'metaData.metaTitle.value') || get(data, 'title.value') || '';
    let last = get(settings,'headerSettings.siteTitle');
    let separator = settings.pageTitleSeparator ? ` ${settings.pageTitleSeparator} ` : ' | ';

    return  [first, last].filter(i => i).join(separator);
  }

  private handleInitialAnchor(anchor: string)
  {
    if (!anchor) return;
    this.viewportScroller.scrollToAnchor(anchor);
  }

  private getUtmParams(data: IPageData) {
    return data && data.utmConfiguration ? data.utmConfiguration.value.params : {};
  }

  private updatePageBackground() {
    const main = document.getElementsByTagName("main")[0];
    const screenHeight = window.screen.height;
    const backgroundContainer = document.getElementById("main-bg-image");
    if (
      this.data.pageBackgroundImage &&
      this.data.pageBackgroundImage.backgroundImageImage &&
      this.data.pageBackgroundImage.backgroundImageImage.value &&
      this.data.pageBackgroundImage.backgroundImageImage.value.src
    ) {
      backgroundContainer.style.height = screenHeight + 'px';
      backgroundContainer.style.backgroundImage = `url(${this.data.pageBackgroundImage.backgroundImageImage.value.src})`;

      if (!this.data.pageBackgroundImage.backgroundImageRemoveOverlay.value) {
        main.classList.add("main--overlay");
      }
    } else {
      backgroundContainer.style.backgroundImage = '';
      main.classList.remove("main--overlay");
    }
  }
}
